import { API_PATH } from '../constants/api';
import { fetchApi } from '../http-request';
import { DynamicObject, TPaginationParams } from '../types/api/commons';
import {
  TParamApiGetWaypoints,
  TParamsGetLocationByLatLongBy,
  TPayloadCreateLoc,
  TPayloadGetCity,
  TPayloadKeyword,
  TPayloadLocationDetails,
  TPayloadMasterLocation,
} from '../types/api/location';
import {
  TParamsGetCargoTypes,
  TParamsPostNewCargoType,
  TPayloadBulkLocation,
  TPayloadPreInsert,
} from '../types/api/shared';
import { cleanEmptyKeyObject, cleanObject } from '../utils';

export const getLatestVersion = async () => {
  try {
    const response = await fetchApi(API_PATH.shared.appVersion);
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return (
      data?.data || {
        latestVersion: null,
      }
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getLocationByLatLong = async (
  payload: TParamsGetLocationByLatLongBy
) => {
  try {
    const response = await fetchApi.get(API_PATH.shared.nominatimReverse, {
      params: {
        ...payload,
        format: 'geocodejson',
        'accept-language': 'id',
      },
      ...(payload.token ? { headers: { 'X-Api-Key': payload.token } } : {}),
    });

    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    if (payload.isReturnGeocoding) {
      return response?.data?.features?.[0]?.properties?.geocoding || {};
    }

    return response?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getRouteDrivingByLatLong = async ({
  origin,
  additionalPoint = '',
  destination,
}: TParamApiGetWaypoints) => {
  const additionalWays = additionalPoint ? `${additionalPoint};` : '';

  try {
    const response = await fetchApi(
      `${API_PATH.shared.nominatimDriving}/${origin};${additionalWays}${destination}`,
      {
        params: {
          steps: true,
          geometries: 'geojson',
        },
      }
    );
    const data = await response.data;

    if (data.code === 'Ok') {
      const waypoints = [];
      const legs = data?.routes?.[0]?.legs;
      for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        for (let j = 0; j < leg.steps.length; j++) {
          const coordinates = leg.steps[j]?.geometry?.coordinates || [];
          for (let k = 0; k < coordinates.length; k++) {
            waypoints.push([coordinates[k][1], coordinates[k][0]]);
          }
        }
      }

      return waypoints;
    } else {
      throw new Error(data?.message);
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getMasterDataCountries = async (payload: DynamicObject) => {
  const params = cleanEmptyKeyObject(payload);
  try {
    const response = await fetchApi.get(API_PATH.shared.country, {
      params,
    });
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data?.data?.countries;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getMasterDataCities = async (payload: TPayloadGetCity) => {
  const params = cleanEmptyKeyObject(payload);
  try {
    const response = await fetchApi.get(API_PATH.shared.cities, {
      params,
    });
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data?.data?.cities;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getMasterDataDistricts = async (payload: TPayloadGetCity) => {
  const params = cleanEmptyKeyObject(payload);
  try {
    const response = await fetchApi.get(API_PATH.shared.districts, {
      params,
    });
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data?.data?.districts;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const createLocationPost = async (payload: TPayloadCreateLoc) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.location, payload, {
      withRequestId: true,
    });
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createBulkLocationPost = async (payload: {
  locations: TPayloadBulkLocation[];
}) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.locations, payload, {
      withRequestId: true,
    });
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getListLocationsRequest = async (
  params: unknown,
  isReturnLocations: boolean = false
) => {
  try {
    const cleanParams = cleanEmptyKeyObject(params);
    const urlParams = new URLSearchParams(cleanParams).toString();

    const response = await fetchApi(
      `${API_PATH.shared.locations}?${urlParams}`
    );

    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }

    if (isReturnLocations) {
      return data?.data?.locations || [];
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getLocationRequest = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(`${API_PATH.shared.location}?${urlParams}`);

  return response.data;
};

export const deleteLocationRequest = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(
      API_PATH.shared.deleteLocation,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const validateMasterLocationsBulk = async (payload: {
  locationNames: string[];
}) => {
  try {
    const response = await fetchApi.post(
      API_PATH.shared.validateLocationName,
      payload,
      {
        withRequestId: true,
      }
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const preInsertMasterLocationsBulk = async (
  payload: TPayloadPreInsert
) => {
  try {
    const response = await fetchApi.post(
      API_PATH.shared.preInsertLocations,
      payload,
      {
        withRequestId: true,
      }
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateLocation = async (
  payload: TPayloadCreateLoc & { id: string; cityId?: string }
) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.location, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getAllCityDistrict = async () => {
  try {
    const response = await fetchApi.get(API_PATH.shared.cityDistrict);
    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data?.data?.data || response?.data?.data?.cities || [];
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getLocationByKeyword = async (payload: TPayloadKeyword) => {
  try {
    const cleanParams = cleanEmptyKeyObject(payload);

    const response = await fetchApi.get(API_PATH.shared.googleSearch, {
      params: {
        ...cleanParams,
        addressdetails: 1,
        format: 'geocodejson',
        'accept-language': 'id',
      },
    });

    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getLocationDetails = async (payload: TPayloadLocationDetails) => {
  try {
    const cleanParams = cleanEmptyKeyObject(payload);

    const response = await fetchApi.get(API_PATH.shared.googlePlacesDetails, {
      params: {
        ...cleanParams,
      },
    });

    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getMasterLocationByKeyword = async (
  payload: TPayloadMasterLocation
) => {
  try {
    const cleanParams = cleanEmptyKeyObject(payload);

    const response = await fetchApi.get(API_PATH.shared.locationSearch, {
      params: {
        ...cleanParams,
      },
    });

    if ([200, 201].indexOf(response.status) === -1) {
      throw new Error(response?.data?.message);
    }

    return response?.data.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getConfiguration = async (keys: string) => {
  const response = await fetchApi(`${API_PATH.shared.config}?keys=${keys}`);

  return response.data.data || {};
};

export const getOrgConfiguration = async (configName: string) => {
  try {
    const response = await fetchApi(
      `${API_PATH.shared.orgConfig}?configName=${configName}`
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getListGroupZoneRequest = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(
    `${API_PATH.shared.zoneGroupList}?${urlParams}`
  );

  return response.data;
};

export const getListZonesRequest = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(`${API_PATH.shared.zones}?${urlParams}`);

  return response.data;
};

export const getFleetZoneOptions = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(
    `${API_PATH.shared.getFleetZone}?${urlParams}`
  );

  return response.data;
};

export const createZone = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.zone, payload, {
      withRequestId: true,
    });
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateZone = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.zone, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const deleteZones = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.deleteZones, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateZoneFleetPriority = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(
      API_PATH.shared.zoneFleetPriority,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getListZoneTemplateRequest = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(
    `${API_PATH.shared.zoneTemplates}?${urlParams}`
  );

  return response.data;
};

export const getListZoneTemplateDetailRequest = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(
    `${API_PATH.shared.zoneTemplate}?${urlParams}`
  );

  return response.data;
};

export const createGroupZoneRequest = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.zoneGroup, payload, {
      withRequestId: true,
    });
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const deleteGroupZoneRequest = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(
      API_PATH.shared.zoneGroupDeletes,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const editGroupZoneRequest = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.zoneGroup, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const cloneGroupZoneRequest = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(
      API_PATH.shared.zoneGroupClone,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const convertUploadOrderTemplate = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(
      API_PATH.shared.templateConverter,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getFleetClusterOptions = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(
    `${API_PATH.shared.getFleetCluster}?${urlParams}`
  );

  return response.data;
};

export const getFleetDriverOptions = async (params: unknown) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(`${API_PATH.fleet.drivers}?${urlParams}`);

  return response.data;
};

export const getCargoTypes = async (params: TParamsGetCargoTypes) => {
  try {
    const cleanParams = cleanObject(params);
    const urlParams = new URLSearchParams(cleanParams).toString();
    const response = await fetchApi(
      `${API_PATH.shared.getCargoTypes}?${urlParams}`
    );
    const data = response?.data;
    if ([200, 201].indexOf(data?.statusCode) === -1) {
      throw new Error(data?.message);
    }
    if (params?.isGetLoadType) {
      return data?.data?.cargoTypes;
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const postNewCargoType = async (value: TParamsPostNewCargoType) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.cargoTypes, value, {
      withRequestId: true,
    });
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const deleteCargoType = async (payload: {
  ids: string[];
  isDelete?: boolean;
}) => {
  try {
    const response = await fetchApi.put(
      API_PATH.shared.deleteCargoTypes,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data?.cargoTypes;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getListMasterSKU = async (
  params: unknown,
  isReturnSKUList: boolean = false
) => {
  const cleanParams = cleanObject(params);
  const urlParams = new URLSearchParams(cleanParams).toString();

  const response = await fetchApi(`${API_PATH.shared.sku}?${urlParams}`);

  if (isReturnSKUList) {
    return response?.data?.data?.skuList;
  }

  return response?.data?.data;
};

export const getTemplateSku = async () => {
  const response = await fetchApi(API_PATH.shared.skuGenerateTemplate);
  return response.data?.data;
};

export const postValidateMasterSku = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.skuValidate, payload, {
      withRequestId: true,
    });
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const postSubmitMasterSku = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(API_PATH.shared.skuAdd, payload, {
      withRequestId: true,
    });
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const deleteMasterSku = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.skuDelete, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const editMasterSku = async (payload: unknown) => {
  try {
    const response = await fetchApi.put(API_PATH.shared.sku, payload);
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getUoms = async (groupUoms: string) => {
  const response = await fetchApi(
    `${API_PATH.shared.uoms}?groupUoms=${groupUoms}`
  );
  return response?.data;
};

export const getListBank = async (params: TPaginationParams) => {
  const paramsClean = cleanEmptyKeyObject(params);

  try {
    const response = await fetchApi(`${API_PATH.shared.banks}`, {
      params: paramsClean,
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data?.list || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getDashboardDriver = async () => {
  try {
    const response = await fetchApi(`${API_PATH.shared.dashboard}?type=driver`);
    return response.data?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getDashboardTruck = async () => {
  try {
    const response = await fetchApi(`${API_PATH.shared.dashboard}?type=truck`);
    return response.data?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getInvoiceVendorCustomer = async (params: {
  keyword?: string;
  orgType?: string;
}) => {
  try {
    const cleanParams = cleanEmptyKeyObject(params);
    const response = await fetchApi(
      `${API_PATH.shared.invoiceVendorCustomer}`,
      {
        params: cleanParams,
      }
    );

    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data.data.list;
  } catch (error) {
    return Promise.reject(error);
  }
};
